import { useState, useEffect } from 'react';
import { FileText, Code, Eye, BarChart3, Download, Copy, Check, AlertTriangle, Clock, Hash } from 'lucide-react';
import MarkdownPreview from './MarkdownPreview';
import HtmlPreview from './HtmlPreview';
import QualityReport from './QualityReport';
import { getDownloadUrl, getTaskStatus } from '../services/api';
import { formatDuration } from '../utils/format';

const TABS = [
  { id: 'preview', label: 'Apercu', icon: Eye },
  { id: 'markdown', label: 'Markdown', icon: Code },
  { id: 'html', label: 'HTML', icon: FileText },
  { id: 'quality', label: 'Qualite', icon: BarChart3 },
];

function getDirection(task, result) {
  const explicit = result?.direction || result?.metadata?.direction;
  if (explicit) return explicit;
  return task?.options?.lang === 'ar' ? 'rtl' : 'ltr';
}

function getLanguage(task, result) {
  return result?.language || result?.metadata?.language || task?.options?.lang || 'fr';
}

export default function ResultViewer({ task, onTaskUpdate }) {
  const [activeTab, setActiveTab] = useState('preview');
  const [copied, setCopied] = useState(false);
  const [copiedId, setCopiedId] = useState(false);

  // Re-fetch result if task is completed but result is missing (race condition fix)
  useEffect(() => {
    if (task && task.status === 'completed' && !task.result) {
      getTaskStatus(task.taskId).then((data) => {
        if (data.result && onTaskUpdate) {
          onTaskUpdate(task.taskId, {
            status: data.status,
            result: data.result,
            quality_report: data.quality_report,
          });
        }
      }).catch(() => {});
    }
  }, [task?.taskId, task?.status, task?.result, onTaskUpdate]);

  if (!task) {
    return (
      <div className="result-empty">
        <FileText size={48} className="result-empty-icon" />
        <p>Selectionnez une tache pour voir les resultats</p>
      </div>
    );
  }

  if (task.status === 'queued' || task.status === 'processing') {
    const elapsed = task.createdAt
      ? Math.round((Date.now() - new Date(task.createdAt).getTime()) / 1000)
      : 0;

    return (
      <div className="result-empty">
        <div className="async-status-card">
          <div className="async-spinner-wrapper">
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2">
              <path d="M21 12a9 9 0 11-6.219-8.56" />
            </svg>
          </div>
          <div className="async-info">
            <h3>{task.status === 'queued' ? 'En file d\'attente...' : 'Traitement en cours...'}</h3>
            <div className="async-details">
              <div className="async-detail-row">
                <Hash size={13} />
                <span className="async-task-id" title={task.taskId}>
                  {task.taskId}
                </span>
                <button
                  className="btn-icon"
                  onClick={() => {
                    navigator.clipboard.writeText(task.taskId);
                    setCopiedId(true);
                    setTimeout(() => setCopiedId(false), 1500);
                  }}
                  title="Copier le taskId"
                >
                  {copiedId ? <Check size={12} /> : <Copy size={12} />}
                </button>
              </div>
              <div className="async-detail-row">
                <FileText size={13} />
                <span>{task.fileName}</span>
              </div>
              {task.options?.lang && (
                <div className="async-detail-row">
                  <span className="async-lang-badge">{task.options.lang.toUpperCase()}</span>
                  <span>Langue OCR</span>
                </div>
              )}
              <div className="async-detail-row">
                <Clock size={13} />
                <span>Temps ecoule : {elapsed}s</span>
              </div>
            </div>
            {task.status === 'processing' && (
              <div className="async-progress">
                <div className="progress-bar-track" style={{ height: 6 }}>
                  <div
                    className="progress-bar-fill"
                    style={{ width: `${task.progress || 5}%` }}
                  />
                </div>
                <span className="async-progress-label">{task.progress || 5}%</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (task.status === 'failed') {
    return (
      <div className="result-empty">
        <AlertTriangle size={48} style={{ color: 'var(--danger)', opacity: 0.6 }} />
        <p style={{ color: 'var(--danger)' }}>Echec du traitement</p>
        <div className="async-detail-row" style={{ marginTop: 8 }}>
          <Hash size={13} />
          <span className="async-task-id" title={task.taskId} style={{ color: 'var(--text-dim)' }}>
            {task.taskId}
          </span>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-dim)', maxWidth: 400, textAlign: 'center' }}>
          {task.error || 'Une erreur inconnue est survenue.'}
        </p>
      </div>
    );
  }

  const result = task.result;
  const direction = getDirection(task, result);
  const language = getLanguage(task, result);
  if (!result) {
    return (
      <div className="result-empty">
        <p>Aucun resultat disponible</p>
      </div>
    );
  }

  const handleCopy = async () => {
    const text = activeTab === 'html' ? result.html : result.markdown;
    if (text) {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <>
      <div className="result-tabs">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            className={`result-tab ${activeTab === id ? 'active' : ''}`}
            onClick={() => setActiveTab(id)}
          >
            <Icon size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
            {label}
          </button>
        ))}
      </div>

      <div className="result-toolbar">
        <span className="result-task-id" title={task.taskId}>
          <Hash size={12} />
          {task.taskId.substring(0, 8)}
        </span>
        {task.options?.lang && (
          <span className="result-lang-badge">{task.options.lang.toUpperCase()}</span>
        )}
        {direction === 'rtl' && (
          <span className="result-lang-badge">RTL</span>
        )}
        {result.processingTimeMs && (
          <span style={{ fontSize: 12, color: 'var(--text-dim)', marginRight: 'auto' }}>
            Traite en {formatDuration(result.processingTimeMs)}
          </span>
        )}
        {(activeTab === 'preview' || activeTab === 'markdown' || activeTab === 'html') && (
          <>
            <button className="btn btn-sm" onClick={handleCopy}>
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'Copie' : 'Copier'}
            </button>
            <a
              className="btn btn-sm"
              href={getDownloadUrl(task.taskId)}
              download
            >
              <Download size={14} />
              Telecharger .md
            </a>
          </>
        )}
      </div>

      <div className="result-content">
        {activeTab === 'preview' && (
          <MarkdownPreview content={result.markdown} direction={direction} />
        )}
        {activeTab === 'markdown' && (
          <pre className={`raw-view ${direction === 'rtl' ? 'rtl' : ''}`} dir={direction}>
            {result.markdown}
          </pre>
        )}
        {activeTab === 'html' && (
          <HtmlPreview html={result.html} direction={direction} language={language} />
        )}
        {activeTab === 'quality' && (
          <QualityReport report={task.quality_report} />
        )}
      </div>
    </>
  );
}
